import type { OptionPosition, OptionType, StrategyKind } from '../types'

export interface StrategyOptionLeg {
  optionType: OptionType
  position: OptionPosition
  label: string
}

export interface StrategyMeta {
  label: string
  shortLabel: string
  /** Primary leg retained for single-leg visual defaults. */
  optionType: OptionType
  optionLegs: StrategyOptionLeg[]
  underlyingSign: -1 | 0 | 1
  benchmarkLabel: string
  benchmarkSign: -1 | 0 | 1
  risk: string
  description: string
}

export const STRATEGY_META: Record<StrategyKind, StrategyMeta> = {
  covered_call: {
    label: 'Covered Call',
    shortLabel: 'Covered Call',
    optionType: 'call',
    optionLegs: [{ optionType: 'call', position: 'short', label: 'Short Call' }],
    underlyingSign: 1,
    benchmarkLabel: 'Long Stock',
    benchmarkSign: 1,
    risk: '下跌风险仍接近持有正股；上涨收益会被 Call 执行价截断。',
    description: '持有正股，同时周期性卖出 Call 收取权利金。',
  },
  naked_call: {
    label: 'Naked Short Call',
    shortLabel: 'Naked Call',
    optionType: 'call',
    optionLegs: [{ optionType: 'call', position: 'short', label: 'Short Call' }],
    underlyingSign: 0,
    benchmarkLabel: 'Cash',
    benchmarkSign: 0,
    risk: '理论亏损无限。模拟器不会替代券商保证金、强平或风控模型。',
    description: '不持有对应正股，直接卖出 Call。上涨尾部风险非常大。',
  },
  covered_put: {
    label: 'Covered Put',
    shortLabel: 'Covered Put',
    optionType: 'put',
    optionLegs: [{ optionType: 'put', position: 'short', label: 'Short Put' }],
    underlyingSign: -1,
    benchmarkLabel: 'Short Stock',
    benchmarkSign: -1,
    risk: 'Covered Put = Short Stock + Short Put；股价上涨时 Short Stock 的理论亏损无限。',
    description: '做空正股，同时卖出 Put；Put 权利金换取下跌方向的收益封顶。',
  },
  naked_put: {
    label: 'Naked Short Put',
    shortLabel: 'Naked Put',
    optionType: 'put',
    optionLegs: [{ optionType: 'put', position: 'short', label: 'Short Put' }],
    underlyingSign: 0,
    benchmarkLabel: 'Cash',
    benchmarkSign: 0,
    risk: '标的大跌时可能发生大额亏损；若需要严格现金担保，请使用 Cash-Secured Put。',
    description: '不持有/做空对应正股，直接卖出 Put 收取权利金。',
  },
  cash_secured_put: {
    label: 'Cash-Secured Put',
    shortLabel: 'Cash-Secured Put',
    optionType: 'put',
    optionLegs: [{ optionType: 'put', position: 'short', label: 'Short Put' }],
    underlyingSign: 0,
    benchmarkLabel: 'Cash',
    benchmarkSign: 0,
    risk: '以现金覆盖执行价义务；最大经济损失仍可能接近全部担保现金。',
    description: '卖出 Put，但每次开仓都强制要求 K × shares 不超过可用现金，不允许隐含杠杆。',
  },
  protective_put: {
    label: 'Protective Put',
    shortLabel: 'Protective Put',
    optionType: 'put',
    optionLegs: [{ optionType: 'put', position: 'long', label: 'Long Put' }],
    underlyingSign: 1,
    benchmarkLabel: 'Long Stock',
    benchmarkSign: 1,
    risk: 'Put 为下跌提供保护，但持续滚动买入保护会产生权利金与交易成本。',
    description: '持有正股，同时周期性买入 Put，限制下跌尾部风险。',
  },
  short_stock_short_call: {
    label: 'Short Stock + Short Call',
    shortLabel: 'Short Stock + Short Call',
    optionType: 'call',
    optionLegs: [{ optionType: 'call', position: 'short', label: 'Short Call' }],
    underlyingSign: -1,
    benchmarkLabel: 'Short Stock',
    benchmarkSign: -1,
    risk: '正股做空和 Short Call 都暴露于上涨风险，极端上涨时亏损可非常大。',
    description: '做空正股并额外卖出 Call；适合研究偏空且卖波动率的叠加暴露。',
  },
  collar: {
    label: 'Collar',
    shortLabel: 'Collar',
    optionType: 'put',
    optionLegs: [
      { optionType: 'put', position: 'long', label: 'Long Put' },
      { optionType: 'call', position: 'short', label: 'Short Call' },
    ],
    underlyingSign: 1,
    benchmarkLabel: 'Long Stock',
    benchmarkSign: 1,
    risk: 'Long Put 限制下跌，Short Call 限制上涨；净成本取决于两个期权腿的相对定价。',
    description: '持有正股，同时买 Put、卖 Call，形成有下限也有上限的区间结构。',
  },
  long_straddle: {
    label: 'Long Straddle',
    shortLabel: 'Long Straddle',
    optionType: 'call',
    optionLegs: [
      { optionType: 'call', position: 'long', label: 'Long Call' },
      { optionType: 'put', position: 'long', label: 'Long Put' },
    ],
    underlyingSign: 0,
    benchmarkLabel: 'Cash',
    benchmarkSign: 0,
    risk: '最大损失通常为净权利金与成本；需要足够大的价格波动覆盖时间价值损耗。',
    description: '同时买入 Call 与 Put，做多波动率和尾部移动。',
  },
  short_straddle: {
    label: 'Short Straddle',
    shortLabel: 'Short Straddle',
    optionType: 'call',
    optionLegs: [
      { optionType: 'call', position: 'short', label: 'Short Call' },
      { optionType: 'put', position: 'short', label: 'Short Put' },
    ],
    underlyingSign: 0,
    benchmarkLabel: 'Cash',
    benchmarkSign: 0,
    risk: '卖出双边波动率；上涨方向理论亏损无限，下跌方向也可能发生大额亏损。',
    description: '同时卖出 Call 与 Put，收取双边权利金。',
  },
  risk_reversal: {
    label: 'Risk Reversal · Long Call + Short Put',
    shortLabel: 'Risk Reversal',
    optionType: 'call',
    optionLegs: [
      { optionType: 'call', position: 'long', label: 'Long Call' },
      { optionType: 'put', position: 'short', label: 'Short Put' },
    ],
    underlyingSign: 0,
    benchmarkLabel: 'Cash',
    benchmarkSign: 0,
    risk: '方向性偏多结构；Short Put 带来显著下跌风险，Long Call 提供上涨凸性。',
    description: '同时买 Call、卖 Put；相同行权价时经济上接近合成多头远期。',
  },
  reverse_risk_reversal: {
    label: 'Reverse Risk Reversal · Short Call + Long Put',
    shortLabel: 'Reverse Risk Reversal',
    optionType: 'put',
    optionLegs: [
      { optionType: 'call', position: 'short', label: 'Short Call' },
      { optionType: 'put', position: 'long', label: 'Long Put' },
    ],
    underlyingSign: 0,
    benchmarkLabel: 'Cash',
    benchmarkSign: 0,
    risk: '方向性偏空结构；Short Call 暴露于理论无限上涨亏损，Long Put 提供下跌凸性。',
    description: '同时卖 Call、买 Put；相同行权价时经济上接近合成空头远期。',
  },
}

export function getStrategyMeta(strategy: StrategyKind): StrategyMeta {
  return STRATEGY_META[strategy]
}

export function isNakedStrategy(strategy: StrategyKind): boolean {
  const meta = STRATEGY_META[strategy]
  return meta.underlyingSign === 0 && meta.optionLegs.some((leg) => leg.position === 'short')
}

export function needsTailRiskWarning(strategy: StrategyKind): boolean {
  return [
    'naked_call',
    'covered_put',
    'naked_put',
    'cash_secured_put',
    'short_stock_short_call',
    'short_straddle',
    'risk_reversal',
    'reverse_risk_reversal',
  ].includes(strategy)
}
